import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Alert,
  TextField,
  MenuItem,
  Button,
  InputAdornment,
  Stack,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRowParams,
} from '@mui/x-data-grid';
import {
  Article as ArticleIcon,
  CalendarToday as CalendarIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { getPublicationStats, getPublications, triggerPublicationIngestion } from '../../services/adminApi';
import type {
  PublicationStatsDto,
  PublicationListDto,
  PublicationListQuery,
} from '../../types/publication';

const PublicationMonitoring: React.FC = () => {
  const { t, i18n } = useTranslation('admin');
  const navigate = useNavigate();
  const { token } = useAuth();

  const [stats, setStats] = useState<PublicationStatsDto | null>(null);
  const [publications, setPublications] = useState<PublicationListDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  // Ingestion state
  const [ingestionLoading, setIngestionLoading] = useState(false);
  const [ingestionSuccess, setIngestionSuccess] = useState(false);
  const [ingestionError, setIngestionError] = useState<string | null>(null);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sources, setSources] = useState<string[]>([]);

  // Load statistics
  const loadStats = useCallback(async () => {
    if (!token) return;

    try {
      const statsData = await getPublicationStats(token);
      setStats(statsData);
    } catch (err: any) {
      console.error('Failed to load publication stats:', err);
      // Stats are non-critical, so we don't set error state
    }
  }, [token]);

  // Load publications
  const loadPublications = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      setError(null);

      const query: PublicationListQuery = {
        page: paginationModel.page + 1, // DataGrid uses 0-based, API uses 1-based
        pageSize: paginationModel.pageSize,
        sortBy: 'PublicationDate',
        sortDescending: true,
        searchTerm: searchTerm || undefined,
        source: sourceFilter || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      };

      const result = await getPublications(query, token);
      setPublications(result.items);
      setTotalCount(result.totalCount);

      // Extract unique sources for filter dropdown
      const uniqueSources = Array.from(
        new Set(result.items.map((p) => p.source).filter(Boolean))
      );
      setSources((prev) => {
        const combined = Array.from(new Set([...prev, ...uniqueSources]));
        return combined.sort();
      });
    } catch (err: any) {
      console.error('Failed to load publications:', err);
      setError(err.message || 'Failed to load publications. Please try again.');
      setPublications([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [paginationModel, searchTerm, sourceFilter, dateFrom, dateTo, token]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadPublications();
  }, [loadPublications]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setPaginationModel((prev) => ({ ...prev, page: 0 })); // Reset to first page on search
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleClearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setSourceFilter('');
    setDateFrom('');
    setDateTo('');
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleTriggerIngestion = async () => {
    if (!token) return;

    try {
      setIngestionLoading(true);
      setIngestionError(null);
      
      await triggerPublicationIngestion(token);
      
      setIngestionSuccess(true);
      // Reload stats and publications after successful ingestion
      await loadStats();
      await loadPublications();
    } catch (err: any) {
      console.error('Failed to trigger publication ingestion:', err);
      setIngestionError(err.response?.data?.message || err.message || 'Failed to trigger ingestion');
    } finally {
      setIngestionLoading(false);
    }
  };

  const handleCloseIngestionSuccess = () => {
    setIngestionSuccess(false);
  };

  const hasActiveFilters = searchTerm || sourceFilter || dateFrom || dateTo;

  const handleRowClick = (params: GridRowParams) => {
    navigate(`/admin/publications/${params.row.id}`);
  };

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: t('publications.list.columns.title'),
      flex: 2,
      minWidth: 250,
    },
    {
      field: 'source',
      headerName: t('publications.list.columns.source'),
      width: 150,
    },
    {
      field: 'publicationDate',
      headerName: t('publications.list.columns.publicationDate'),
      width: 150,
      valueFormatter: (value: string) =>
        new Date(value).toLocaleDateString(i18n.language),
    },
    {
      field: 'referenceId',
      headerName: t('publications.list.columns.referenceId'),
      width: 180,
    },
    {
      field: 'mentionCount',
      headerName: t('publications.list.columns.mentionCount'),
      width: 120,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'createdAt',
      headerName: t('publications.list.columns.createdAt'),
      width: 150,
      valueFormatter: (value: string) =>
        new Date(value).toLocaleDateString(i18n.language),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t('publications.title')}
      </Typography>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ArticleIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" component="div">
                    {t('publications.stats.totalCount')}
                  </Typography>
                </Box>
                <Typography variant="h3" color="primary">
                  {stats.totalCount.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CalendarIcon sx={{ mr: 1, color: 'secondary.main' }} />
                  <Typography variant="h6" component="div">
                    {t('publications.stats.oldestDate')}
                  </Typography>
                </Box>
                <Typography variant="h5" color="secondary">
                  {stats.oldestPublicationDate
                    ? new Date(stats.oldestPublicationDate).toLocaleDateString(
                        i18n.language
                      )
                    : t('publications.stats.noData')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ViewIcon sx={{ mr: 1, color: 'info.main' }} />
                  <Typography variant="h6" component="div">
                    {t('publications.stats.oldestPublication')}
                  </Typography>
                </Box>
                <Typography variant="body1" color="info.main">
                  {stats.oldestPublicationTitle ||
                    t('publications.stats.noData')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">{t('publications.filters.title')}</Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={ingestionLoading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
              onClick={handleTriggerIngestion}
              disabled={ingestionLoading}
            >
              {ingestionLoading ? t('publications.ingestion.running') : t('publications.ingestion.trigger')}
            </Button>
          </Box>
          
          <TextField
            fullWidth
            placeholder={t('publications.filters.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            helperText={t('publications.filters.search')}
          />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select
                fullWidth
                label={t('publications.filters.source')}
                value={sourceFilter}
                onChange={(e) => {
                  setSourceFilter(e.target.value);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
              >
                <MenuItem value="">
                  {t('publications.filters.sourcePlaceholder')}
                </MenuItem>
                {sources.map((source) => (
                  <MenuItem key={source} value={source}>
                    {source}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                type="date"
                label={t('publications.filters.dateFrom')}
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                type="date"
                label={t('publications.filters.dateTo')}
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                disabled={!hasActiveFilters}
                sx={{ height: '100%' }}
              >
                {t('publications.filters.clearFilters')}
              </Button>
            </Grid>
          </Grid>
        </Stack>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Ingestion Error Alert */}
      {ingestionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setIngestionError(null)}>
          {t('publications.ingestion.error')}: {ingestionError}
        </Alert>
      )}

      {/* Publications Data Grid */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={publications}
          columns={columns}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          rowCount={totalCount}
          paginationMode="server"
          onRowClick={handleRowClick}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
          }}
          localeText={{
            noRowsLabel: t('publications.list.empty'),
          }}
        />
      </Paper>

      {/* Success Snackbar */}
      <Snackbar
        open={ingestionSuccess}
        autoHideDuration={6000}
        onClose={handleCloseIngestionSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseIngestionSuccess} severity="success" sx={{ width: '100%' }}>
          {t('publications.ingestion.success')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PublicationMonitoring;
